import React from 'react';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';

import { ProductLineItemCard } from '@garnish/product';

import {
  useLineItemDescription,
  useLineItemIngredients,
  useLineItemName,
  useLineItemPrice,
} from '@order/shared-hooks';

import { type PartialLineItem } from '../../graphql/partials';

// ────────────────────────────────────────────────────────────────────────────────

export const ReceiptLineItemCard = (props: ReceiptLineItemCardProps) => {
  const { lineItem } = props;
  const { id = '', quantity = 0, product } = lineItem;

  const { formatMessage } = useIntl();

  const lineItemName = useLineItemName(product, lineItem);
  const lineItemA11yLabel = formatMessage(messages.lineItemA11yLabel, { name: lineItemName }); // prettier-ignore
  const lineItemPrice = useLineItemPrice(lineItem);
  const lineItemDescription = useLineItemDescription(lineItem);
  const lineItemAssetUrl = product?.asset?.url ?? '';

  const { addedIngredients, removedIngredients } = useLineItemIngredients(lineItem);

  // ────────────────────────────────────────────────────────────────────

  return (
    <ProductLineItemCard
      productId={id}
      productName={lineItemName}
      price={lineItemPrice}
      cloudinaryImageUrl={lineItemAssetUrl}
      imageSize="large"
      addedIngredients={addedIngredients}
      addedIngredientsLabel={formatMessage(messages.productAddedIngredientsLabel)} // prettier-ignore
      removedIngredients={removedIngredients}
      removedIngredientsLabel={formatMessage(messages.productRemovedIngredientsLabel)} // prettier-ignore
      description={lineItemDescription}
      quantity={quantity}
      isDisabled
      accessibilityLabel={lineItemA11yLabel}
    />
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly removeBtnLabel: NoMessageValues;
  readonly successfulReorderMessage: { readonly product: MessageValue };
  readonly productAddedIngredientsLabel: NoMessageValues;
  readonly productRemovedIngredientsLabel: NoMessageValues;
  readonly lineItemA11yLabel: { readonly name: MessageValue };
}>({
  removeBtnLabel: {
    defaultMessage: 'Remove',
    description: 'Order Status | Line Item Card | Remove button label',
  },
  successfulReorderMessage: {
    defaultMessage: '1x {product} added to your bag!',
    description: 'Order Status | Line Item Card | Successful reorder message',
  },
  productAddedIngredientsLabel: {
    defaultMessage: 'Added:',
    description: 'Order Status | Line Item Card | Product added ingredients label',
  },
  productRemovedIngredientsLabel: {
    defaultMessage: 'Removed:',
    description: 'Order Status | Line Item Card | Product removed ingredients label',
  },
  lineItemA11yLabel: {
    defaultMessage: 'Navigate to {name} details page',
    description: 'Order Status | Line Item Card | Line item a11y label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LineItem = Omit<PartialLineItem, 'ingredients'>;

export type ReceiptLineItemCardProps = {
  lineItem: LineItem;
};
